/**
 * AI wrong — por qué la cocina entrega un pedido que el análisis marcó incompleto.
 *
 * Hoy, cuando el análisis dice que falta un producto, la cocina tiene dos salidas: corregir y
 * hacer otra foto, o entregar sin corregir. La segunda no deja rastro de POR QUÉ, y ese porqué es
 * justo lo que separa un fallo del modelo de un problema de configuración, de packaging o de
 * stock. El resultado es una métrica de "IA wrong" que no dice qué arreglar: el 9 % de los pedidos
 * pasa por aquí y no sabemos cuál de las cuatro causas es.
 *
 * Así que "entregar sin corregir" gana una segunda pregunta con tres respuestas, y cada una manda
 * un mensaje a soporte. Tres, no más: las causas que la cocina puede distinguir de un vistazo sin
 * ponerse a escribir. No se ofrece "no me sale de ahí ponerlo", que es la respuesta honesta de
 * algunos casos pero no es accionable.
 *
 * Este módulo es solo la definición y el mensaje, puro y con i18n, como helpFlow.js: quien manda
 * (App.vue) tiene el contexto y el messenger. Es también la semilla del flujo completo de IA wrong
 * que reutilizará el motor de guidedFlow.js cuando se pase de tres botones a un flujo con pasos.
 */

/**
 * Las tres razones, en el orden en que se ofrecen. `bodyKey` es la frase que viaja a soporte, en
 * la voz de la cocina; `labelKey` es el botón.
 */
export const AI_WRONG_REASONS = Object.freeze([
  {
    // La que de verdad significa "la IA se ha equivocado".
    id: 'was_there',
    // Un ojo tachado: "estaba, no lo vio". Los tres iconos son de forma muy distinta a propósito;
    // con tres frases parecidas, la silueta es lo que se distingue antes de leer.
    icon: 'pi-eye-slash',
    labelKey: 'message.aiWrongWasThere',
    bodyKey: 'message.aiWrongMsgWasThere',
  },
  {
    // La que ops espera que se pulse de más: es la que suena a excusa aceptable, y muchas veces
    // esconde un packaging que no está configurado como el modelo lo espera.
    id: 'different_packaging',
    icon: 'pi-box',
    labelKey: 'message.aiWrongDifferentPackaging',
    bodyKey: 'message.aiWrongMsgDifferentPackaging',
  },
  {
    // Ni fallo del modelo ni packaging: no había producto. El pedido sale incompleto de verdad.
    id: 'out_of_stock',
    icon: 'pi-ban',
    labelKey: 'message.aiWrongOutOfStock',
    bodyKey: 'message.aiWrongMsgOutOfStock',
  },
]);

/**
 * ¿Está encendido el piloto para esta tablet?
 *
 * Vive aquí y no en el componente porque lo leen dos sitios —la pantalla que pregunta y el registro
 * de la decisión— y si se separan, el brazo que se apunta en la telemetría deja de ser el que se ha
 * pintado y el experimento mide humo. El override local existe para probar en una tablet concreta
 * sin tocar el flag, igual que `bronze_help_send_direct`.
 *
 * @param {*} posthog  el cliente de PostHog (inyectado para poder testarlo)
 * @param {*} storage  localStorage (idem)
 */
export function isAiWrongReasonsEnabled(posthog, storage) {
  try {
    const override = storage?.getItem('bronze_ai_wrong_reasons');
    if (override === '1') return true;
    if (override === '0') return false;
  } catch (_) { /* sin storage manda el flag */ }
  try {
    return posthog?.isFeatureEnabled('ai-wrong-reasons') === true;
  } catch (_) {
    return false;
  }
}

export function findAiWrongReason(id) {
  return AI_WRONG_REASONS.find((reason) => reason.id === id) || null;
}

/**
 * El paso de motivos se pinta justo donde estaba "entregar sin corregir". Un segundo toque de
 * impaciencia arrastrado desde ese botón cae sobre lo que aparece en esa posición —un motivo o
 * "Cerrar"—; si es un motivo, contamina el reparto (la métrica primaria del piloto), y si es
 * "Cerrar", baja la tasa de respuesta. A menos de este margen desde que el paso se pinta no es una
 * respuesta, es el rebote del toque anterior. El margen es más corto que leer tres opciones nuevas,
 * así que no come respuestas reales. Vive aquí, y no como número suelto en el componente, para poder
 * probarlo y para que la constante del tratamiento esté con el resto del piloto.
 */
export const REASON_TAP_GUARD_MS = 350;

export function isReasonTapSettling(askingReasonAt, now = Date.now(), guardMs = REASON_TAP_GUARD_MS) {
  return askingReasonAt != null && (now - askingReasonAt) < guardMs;
}

/**
 * Los productos que el análisis dijo que faltaban, en texto plano para el mensaje. Se envían
 * porque la razón sola no basta: "lo he empaquetado distinto" sobre unas alitas y sobre una bebida
 * son dos problemas de configuración distintos.
 */
export function missingItemNames(missingItems) {
  if (!Array.isArray(missingItems)) return [];
  return missingItems
    .map((item) => {
      const name = item?.groupName || (Array.isArray(item?.name) ? item.name[0] : item?.name);
      if (!name) return null;
      const qty = item?.missingQuantity;
      return qty > 1 ? `${name} x${qty}` : String(name);
    })
    .filter(Boolean);
}

/**
 * El mensaje que se manda a soporte al entregar sin corregir.
 *
 * @param {object} args
 * @param {object} args.reason        una entrada de AI_WRONG_REASONS
 * @param {object|null} args.subject  describeOrder() del pedido
 * @param {string[]} args.items       de missingItemNames()
 * @param {object} args.ctx           de snapshotHelpContext(): tienda, tablet, versión, conexión
 * @param {Function} args.t           traductor de vue-i18n
 */
export function composeAiWrongMessage({ reason, subject = null, items = [], ctx = {}, t }) {
  if (!reason) return null;
  const lines = [`${t('message.helpFlowMsgIntro', { store: ctx.store || '' })} ${t(reason.bodyKey)}`];

  if (items.length) lines.push(t('message.aiWrongMsgItems', { items: items.join(', ') }));

  if (subject) {
    const parts = [t('message.helpFlowMsgOrder', { code: subject.code })];
    if (subject.platformName) parts.push(subject.platformName);
    if (subject.analysisId) parts.push(t('message.helpFlowMsgAnalysis', { id: subject.analysisId }));
    lines.push(parts.join(' · '));
  }

  lines.push('—');
  lines.push(t('message.helpFlowMsgTech', { device: ctx.deviceId || '?', version: ctx.appVersion || '?' }));
  return lines.join('\n');
}

/**
 * El nombre del evento de Intercom. Fijo a propósito: los informes y segmentos se construyen
 * sobre él, y renombrarlo parte la serie histórica.
 */
export const AI_WRONG_EVENT = 'ai_wrong_reason';

/** Recorte defensivo del texto libre; ver el comentario de `note` más abajo. */
const NOTE_MAX = 255;

/**
 * Lo que viaja a Intercom como EVENTO, no como conversación.
 *
 * Aquí no hay nada que responder: la cocina ha contestado una pregunta de un toque y sigue con el
 * pase. Abrirle el messenger encima es una ventana que tiene que cerrar para nada, y una
 * conversación por cada "entregar sin corregir" inunda la bandeja de soporte (es el 9 % de los
 * pedidos). Un evento llega igual a Intercom, sin UI, y se puede segmentar y contar.
 *
 * Dos límites de Intercom mandan la forma de esto: un evento admite 10 claves de metadatos como
 * máximo y, una vez fijadas, no se pueden cambiar. Van siete, y las seis primeras son las
 * accionables — el texto libre es un extra que puede recortarse sin perder nada.
 */
export function aiWrongEventMetadata({ reason, subject = null, items = [], ctx = {}, t }) {
  if (!reason) return null;
  const note = composeAiWrongMessage({ reason, subject, items, ctx, t });
  const fields = {
    reason: reason.id,
    order: subject?.code || null,
    platform: subject?.platformName || null,
    items: items.join(', ') || null,
    analysis: subject?.analysisId || null,
    // La versión va como campo propio porque es el único dato que el recorte del texto libre se
    // llevaría sin remedio: el pedido, la plataforma y el análisis ya son campos, y la tablet es
    // el propio contacto. Y sin versión no se puede decir si un IA wrong sigue vivo tras un
    // despliegue, que es la mitad de para qué sirve medirlo.
    version: ctx.appVersion || null,
    // El mensaje entero en una línea: los metadatos se leen como filas en la ficha del contacto y
    // los saltos de línea se pierden ahí. La longitud máxima de un valor no está documentada, así
    // que recortamos nosotros antes de que lo haga Intercom en silencio.
    note: note ? truncate(note.split('\n').filter((line) => line !== '—').join(' ')) : null,
  };
  // Sin claves vacías: una fila en blanco en la ficha del contacto no dice nada y gasta cupo.
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== null));
}

function truncate(text) {
  return text.length > NOTE_MAX ? `${text.slice(0, NOTE_MAX - 1).trimEnd()}…` : text;
}
